import React from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import { Link } from 'react-router-dom';  // To create links to the player ID page

const PlayerStatsTable = ({ data, position }) => {
  // Define columns based on the position (skater or netminder)
  const columns = React.useMemo(() => {
    if (position === 'skater') {
      // Skater columns
      return [
        {
          Header: 'Player',
          accessor: (row) => `${row.forename} ${row.surname}`, // Combine forename and surname for full name
          Cell: ({ row }) => (
            <Link to={`/player/${row.original.player_id}`} className="group flex flex-row">
              <img
                src={row.original.logo.lg}
                alt={row.original.club_name}
                className="w-6 h-6 inline-block mr-2"
              />
              <p className="max-xs:text-xs text-left transition-colors duration-200 ease-in-out dark:group-hover:text-dark-5">
                {row.original.forename} {row.original.surname}
              </p>
            </Link>
          ),
        },
        { Header: 'GP', accessor: 'total_gp', sortDescFirst: true },
        { Header: 'G', accessor: 'total_goals', sortDescFirst: true },
        { Header: 'A', accessor: 'total_assists', sortDescFirst: true },
        { Header: 'PIM', accessor: 'total_pim', sortDescFirst: true },
        { Header: 'PTS', accessor: 'total_points', sortDescFirst: true },
        {
          Header: 'PPG',
          accessor: 'points_per_game', sortDescFirst: true,
          Cell: ({row}) => (
            <span>{row.original.points_per_game.toFixed(2)}</span>
          )
        },
      ];
    } else {
      // Netminder columns
      return [
        {
          Header: 'Player',
          accessor: (row) => `${row.forename} ${row.surname}`, // Combine forename and surname for full name
          Cell: ({ row }) => (
            <Link to={`/player/${row.original.player_id}`} className="group flex flex-row">
              <img
                  src={row.original.logo.lg}
                  alt={row.original.club_name}
                className="w-6 h-6 inline-block mr-2"
              />
              <p className="transition-colors duration-200 ease-in-out dark:group-hover:text-dark-5">
                {row.original.forename} {row.original.surname}
              </p>
            </Link>
          ),
        },
        { Header: 'GP', accessor: 'total_gp', sortDescFirst: true },
        {
          Header: 'MINS',
          accessor: 'seconds', sortDescFirst: true,
          Cell: ({row}) => (
              <span>{row.original.seconds > 0 ? (String(~~(row.original.seconds / 60)).padStart(2, '0') + ":" + String((row.original.seconds % 60)).padStart(2, '0')) : "-"}</span>
          )
        },
        { Header: 'SA', accessor: 'shots_against', sortDescFirst: true },
        { Header: 'GA', accessor: 'goals_against', sortDescFirst: true },
        { Header: 'SO', accessor: 'shutouts', sortDescFirst: true },
        {
          Header: 'SAV%',
          accessor: 'sav_pct', sortDescFirst: true,
          Cell: ({row}) => (
              <span>{(row.original.sav_pct / 100).toFixed(3)}</span>
          )
        },
        {
          Header: 'GAA',
          accessor: 'gaa',
          Cell: ({row}) => (
              <span>{row.original.gaa.toFixed(2)}</span>
          )
        },
      ];
    }
  }, [position]);

  // Use React Table hooks for sorting and pagination
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page, // Only the rows on the current page
    prepareRow,
    canPreviousPage,
    canNextPage,
    // pageIndex,
    pageSize,
    previousPage,
    nextPage,
    setPageSize,
    state: { pageIndex: currentPage },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0, // Start on the first page
        pageSize: 20, // Display 20 rows per page
        sortBy: [
          {
            id: (position === 'skater' ? 'total_points' : 'sav_pct'),
            desc: true,
          },
        ],
      },
      usePagination: true, // Enable pagination
    },
    useSortBy, // Hook to enable sorting
    usePagination // Hook to handle pagination
  );

  return (
    <div className="overflow-x-auto  pb-2">
      <table {...getTableProps()} className="min-w-full table-auto">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())} // Enabling sorting
                  className="max-xs:text-xs py-1 sm:py-2 px-1 xs:px-2 lg:px-4 border-b dark:border-dark-3 border-light-3 cursor-pointer"
                >
                  {column.render('Header')}
                  <span>
                    {column.isSorted ? (column.isSortedDesc ? ' ↓' : ' ↑') : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps()}
                    className="max-xs:text-xs text-center py-1 sm:py-2 px-2 sm:px-4 border-b dark:border-dark-3 border-light-3"
                  >
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-4 mx-2 ">
        <div>
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="max-xs:text-xs border-buiha-red dark:border-dark-5 font-light p-1 xs:p-2 xs:px-3 mr-1 rounded border-2 hover:bg-buiha-red dark:hover:bg-dark-5 hover:text-white duration-200 ease-in-out"
          >
            Previous
          </button>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="max-xs:text-xs border-buiha-red dark:border-dark-5 p-1 xs:p-2 xs:px-3 rounded border-2 hover:bg-buiha-red dark:hover:bg-dark-5 hover:text-white duration-200 ease-in-out"
          >
            Next
          </button>
        </div>

        <div>
          <p>Page {currentPage + 1}</p>
        </div>

        <div>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="max-xs:text-xs border-buiha-red dark:border-dark-5 bg-transparent p-1 xs:p-2 xs:px-3 rounded border-2 hover:text-white duration-200 ease-in-out"
          >
            {[10, 20, 30, 50].map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default PlayerStatsTable;


